import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import moment from 'moment'
import {
  Alert,
  Button,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SearchIcon from '@mui/icons-material/Search'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import API from '../../../services/API'
import { CRMContext } from '../../CRM'
import SafetyIncidentLogPDF from './SafetyIncidentLogPDF'
import PageHeader from '../../kit/PageHeader'
import StatTile from '../../kit/StatTile'
import dataGridSx from '../../kit/dataGridSx'
import { SoftChip } from '../../kit/Chips'
import { formatDateTime, fromNow } from '../../kit/helpers'
import {
  CLASSIFICATION_COLOR,
  HARM_COLOR,
  STATUS_COLOR,
  isOpen,
  isSeriousHarm,
  overdueActions,
  riskTone,
  slaState,
} from './constants'

const effectiveHarm = (row) =>
  row.triage?.actualHarm && row.triage.actualHarm !== 'Unknown'
    ? row.triage.actualHarm
    : row.reporterHarm

const FILTERS = [
  { key: 'all', predicate: () => true },
  { key: 'open', predicate: (row) => isOpen(row) },
  {
    key: 'unacknowledged',
    predicate: (row) => isOpen(row) && !row.triage?.acknowledgedAt,
  },
  {
    key: 'overdue',
    predicate: (row) => {
      if (!isOpen(row)) return false
      const sla = slaState(row)
      return (
        sla.acknowledge?.key === 'overdue' ||
        sla.classify?.key === 'overdue' ||
        overdueActions(row).length > 0
      )
    },
  },
  {
    key: 'serious',
    predicate: (row) => isOpen(row) && isSeriousHarm(effectiveHarm(row)),
  },
  { key: 'closed', predicate: (row) => !isOpen(row) },
]

const SafetyIncidents = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [search, setSearch] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useContext(CRMContext)

  const filterKey = searchParams.get('filter') || 'open'
  const filter = FILTERS.find((f) => f.key === filterKey) ?? FILTERS[1]
  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'open' || key === filterKey) next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => {
    setLoading(true)
    setError('')
    API.get('/admincrm/clinical-risk/safety-incidents')
      .then((res) => {
        if (res.status === 200) setRows(res.data || [])
        else setError(res.data?.error || 'Could not load the safety incident log.')
      })
      .catch(() => setError('Could not load the safety incident log.'))
      .finally(() => setLoading(false))
  }, [])

  const stats = useMemo(
    () => ({
      open: rows.filter(FILTERS[1].predicate).length,
      unacknowledged: rows.filter(FILTERS[2].predicate).length,
      overdue: rows.filter(FILTERS[3].predicate).length,
      serious: rows.filter(FILTERS[4].predicate).length,
      closed: rows.filter(FILTERS[5].predicate).length,
    }),
    [rows],
  )

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return rows.filter(filter.predicate).filter((row) => {
      if (!query) return true
      return [
        row.incidentID,
        row.title,
        row.tenant?.name,
        row.tenant?.subdomain,
        row.module,
        row.status,
        row.classification,
        row.clickUp?.ticketRef,
        row.reporter?.name,
        ...(row.hazards?.linkedHazardIDs || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [rows, filter, search])

  // The whole log, not the filtered view, so the PDF is a complete record for
  // the safety case. Rendered client-side from the rows already loaded, in
  // the same chrome as the hazard-log PDF.
  const downloadPdf = async () => {
    setDownloading(true)
    setError('')
    try {
      const downloadedBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      const blob = await pdf(
        <SafetyIncidentLogPDF incidents={rows} downloadedBy={downloadedBy} />,
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `AmbuNet_Safety_Incident_Log_${moment().format('YYYY-MM-DD')}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setError('Could not generate the PDF.')
    } finally {
      setDownloading(false)
    }
  }

  const columns = [
    {
      field: 'incidentID',
      headerName: 'ID',
      minWidth: 120,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'title',
      headerName: 'Incident',
      minWidth: 320,
      flex: 1.8,
      renderCell: ({ row }) => (
        <Stack minWidth={0} py={0.5}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}
          >
            {row.title}
          </Typography>
          <Stack direction="row" gap={0.75} alignItems="center" mt={0.5} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {row.tenant?.name || row.tenant?.subdomain || 'Unknown tenant'}
            </Typography>
            {row.module && (
              <SoftChip color="#557A95" label={row.module} sx={{ height: 20, fontSize: 11 }} />
            )}
            {row.clickUp?.ticketRef && (
              <Typography variant="caption" color="text.disabled">
                {row.clickUp.ticketRef}
              </Typography>
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 160,
      flex: 0.6,
      renderCell: ({ value }) => (
        <SoftChip color={STATUS_COLOR[value] || '#64748B'} label={value || 'Unknown'} />
      ),
    },
    {
      field: 'classification',
      headerName: 'Classification',
      minWidth: 150,
      flex: 0.6,
      renderCell: ({ value }) =>
        value && value !== 'Unclassified' ? (
          <SoftChip color={CLASSIFICATION_COLOR[value] || '#64748B'} label={value} />
        ) : (
          <Typography variant="caption" color="text.disabled">
            Unclassified
          </Typography>
        ),
    },
    {
      field: 'harm',
      headerName: 'Harm',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value, row) => effectiveHarm(row) || 'Unknown',
      renderCell: ({ value, row }) => (
        <Tooltip
          title={
            row.triage?.actualHarm && row.triage.actualHarm !== 'Unknown'
              ? 'CSO assessment'
              : 'Reporter assessment'
          }
        >
          <span>
            <SoftChip color={HARM_COLOR[value] || '#64748B'} label={value} />
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'risk',
      headerName: 'Risk',
      minWidth: 100,
      flex: 0.4,
      valueGetter: (value, row) => row.triage?.riskRating || '',
      renderCell: ({ value, row }) =>
        value ? (
          <Tooltip title={`${row.triage?.likelihood} / ${row.triage?.severity}`}>
            <span>
              <SoftChip color={riskTone(value)} label={value} />
            </span>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">
            Not scored
          </Typography>
        ),
    },
    {
      field: 'targets',
      headerName: 'Targets',
      minWidth: 200,
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => {
        if (!isOpen(row)) {
          return (
            <Typography variant="caption" color="text.secondary">
              Closed {fromNow(row.closure?.closedAt)}
            </Typography>
          )
        }
        const sla = slaState(row)
        const late = overdueActions(row).length
        const items = [
          sla.acknowledge?.key !== 'met' ? sla.acknowledge : null,
          sla.classify?.key !== 'met' ? sla.classify : null,
          late ? { key: 'overdue', label: `${late} action${late === 1 ? '' : 's'} overdue` } : null,
        ].filter(Boolean)
        if (!items.length) {
          return (
            <Typography variant="caption" sx={{ color: '#27803B', fontWeight: 600 }}>
              On track
            </Typography>
          )
        }
        return (
          <Stack gap={0.25} py={0.5}>
            {items.map((item) => (
              <Typography
                key={item.label}
                variant="caption"
                sx={{
                  color: item.key === 'overdue' ? '#B42318' : '#B54708',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Stack>
        )
      },
    },
    {
      field: 'reportedAt',
      headerName: 'Reported',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value) => (value ? new Date(value) : null),
      sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
      renderCell: ({ value }) =>
        value ? (
          <Tooltip title={formatDateTime(value)}>
            <Typography variant="body2" color="text.secondary">
              {fromNow(value)}
            </Typography>
          </Tooltip>
        ) : (
          ''
        ),
    },
  ]

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Clinical safety"
        title="Safety Incident Management Log"
        subtitle="Every safety concern and incident reported against AmbuNet, from first report to CSO-signed closure (DCB0129 §3.6.1 and §7.2). Clinical safety tickets raised in the app land here automatically; phoned or emailed reports are logged by hand."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadPdf}
              disabled={downloading || loading || rows.length === 0}
            >
              {downloading ? 'Building PDF…' : 'Download PDF'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => navigate('/safety-incidents/new')}
            >
              Log incident
            </Button>
          </>
        }
      />

      <Grid container spacing={1.5}>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Open"
            value={stats.open}
            caption="Not yet closed"
            icon={<InboxOutlinedIcon />}
            tone="#0F6670"
            active={filterKey === 'open'}
            onClick={() => setFilter('open')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Unacknowledged"
            value={stats.unacknowledged}
            caption="Target: one working day"
            icon={<NotificationsActiveOutlinedIcon />}
            tone={stats.unacknowledged ? '#B42318' : '#64748B'}
            active={filterKey === 'unacknowledged'}
            onClick={() => setFilter('unacknowledged')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Overdue"
            value={stats.overdue}
            caption="A CRMP target or action is late"
            icon={<TimerOutlinedIcon />}
            tone={stats.overdue ? '#B54708' : '#64748B'}
            active={filterKey === 'overdue'}
            onClick={() => setFilter('overdue')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Serious harm"
            value={stats.serious}
            caption="Moderate harm or worse, still open"
            icon={<LocalHospitalOutlinedIcon />}
            tone={stats.serious ? '#7A271A' : '#64748B'}
            active={filterKey === 'serious'}
            onClick={() => setFilter('serious')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Closed"
            value={stats.closed}
            caption="With closure record"
            icon={<TaskAltOutlinedIcon />}
            tone="#27803B"
            active={filterKey === 'closed'}
            onClick={() => setFilter('closed')}
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack gap={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={1}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {visibleRows.length} of {rows.length} {rows.length === 1 ? 'incident' : 'incidents'}
            </Typography>
            {filterKey !== 'all' && (
              <Button size="small" onClick={() => setFilter('all')}>
                Show all
              </Button>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="Search ID, title, tenant, module, hazard…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 360 }, bgcolor: '#FFFFFF' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <DataGrid
          rows={visibleRows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          autoHeight
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: 'reportedAt', sort: 'desc' }] },
          }}
          onRowClick={({ row }) => navigate(`/safety-incidents/${row._id}`)}
          localeText={{
            noRowsLabel: loading
              ? 'Loading…'
              : filterKey === 'open'
                ? 'No open incidents.'
                : 'No incidents match.',
          }}
          sx={{
            ...dataGridSx,
            '& .MuiDataGrid-cell': {
              ...dataGridSx['& .MuiDataGrid-cell'],
              py: 1,
            },
          }}
        />
      </Stack>
    </Stack>
  )
}

export default SafetyIncidents
